import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { EMPTY, Observable, defer } from 'rxjs';
import { catchError, map, retryWhen, delay, take, tap } from 'rxjs/operators';
import { CustomApiModels } from '@mona/api';
import { TokenStorageService } from '@mona/auth';
import { ConfigService } from '@mona/config';
import { DataUpdateMessage, DataUpdateMessageModel } from '@mona/models';
import { WsExtendableService } from '@mona/shared/utils';
import { transformApiDataUpdateMessage } from './transforms/data-update.transforms';

/**
 * PDMS updates ws service - stream listens for Encounter, Ward, Bed update messages
 */
@Injectable({ providedIn: 'root' })
export class PdmsUpdatesWsService extends WsExtendableService<
    CustomApiModels.DataUpdateMessage,
    DataUpdateMessage<DataUpdateMessageModel>
> {
    /**
     * Constructor
     * @param configService ConfigService
     * @param tokenStorage
     */
    constructor(
        private configService: ConfigService,
        private tokenStorage: TokenStorageService,
    ) {
        super();
    }

    /**
     * Connects to ws for encounter updates with simple retry logic
     */
    connect(): Observable<DataUpdateMessage<DataUpdateMessageModel>> {
        return defer(() => {
            // Must disconnect (not only null): retryWhen resubscribes here — nulling alone
            // orphans the previous native socket as a stale 101 in DevTools.
            this.disconnect();
            const accessToken = this.tokenStorage.getAccessToken();
            const url = `${this.configService.get('api.coreWsUrl')}/ws/pdms/general/`;
            this.setConfig(
                url,
                accessToken,
                undefined,
                environment.reconnectInterval,
                0, // outer retryWhen owns recovery — avoid dual native sockets
            );
            return super.connect();
        }).pipe(
            map(message => transformApiDataUpdateMessage(message as CustomApiModels.DataUpdateMessage)),
            retryWhen(errors =>
                errors.pipe(
                    tap(error => console.warn('WebSocket connect failed, retrying...', error)),
                    delay(environment.reconnectInterval),
                    take(environment.reconnectAttempts),
                ),
            ),
            catchError(() => EMPTY),
        );
    }
}
